import Constants from 'utils/Constants';
import NavigationUtils from 'utils/NavigationUtils';
import ComponentUtils from 'utils/ComponentUtils';
import ProcessService from 'services/ProcessService';
import ProcessValidator from 'utils/ProcessValidator';
import ProcessEditor from 'utils/ProcessEditor';
import _ from 'lodash';
import moment from 'moment';
import DodService from 'services/DodService';
import SecurityService from 'services/SecurityService';
import DataExplorerService from 'services/DataExplorerService';
import RunLocation from 'utils/RunLocation';

// Tell eslint that Array is a global var
/*global Array */

/**
 * Utility class of convenience methods for interacting with process objects.
 * None of the methods in ProcessUtils actually modify a process.  They are either read-only accessors,
 * or they create an entirely new process.  Use getEditor() to get an editor for properly modifying an existing
 * process.
 */
export default class ProcessUtils {

    /**
     * Get a ProcessEditor for the given process
     * @param process
     */
    static getEditor(process) {
        return new ProcessEditor(process);
    }

    /**
     * Get a ProcessValidator for the given process
     * @param process
     */
    static getValidator(process) {
        return new ProcessValidator(process);
    }

    /**
     * This is the generic fallback for updating a single field in your process with no special
     * handler logic.
     * @param process
     * @param fieldPath
     * @param value - usually this is the value that comes from an RIE component
     */
    static updateField(process, fieldPath, value) {
        const editor = new ProcessEditor(process);
        editor.updateField(fieldPath, value);
        editor.commit();
    }

    static getProcessUIState(process) {
        return ProcessService.getMyState().processUIStateById[process.name];
    }

    /**
     * @param processId - string
     * @param uiState - object with parameter(s) to update
     */
    static updateProcessUIState(processId, uiState) {
        ProcessService.updateProcessUIState(processId, uiState);
    }

    static shouldShowWizardInputQC(process, parameters) {
        let shouldDisplay = !!this.getProcessUIState(process).showWizardInputQC;
        if (!shouldDisplay) {
            shouldDisplay = this.shouldDisplayTransformationOverride(process, parameters, null);
        }
        return shouldDisplay;
    }

    static shouldShowWizardOutputQC(process, parameters, dimensionName) {
        let shouldDisplay = !!this.getProcessUIState(process).showWizardOutputQC;
        if (!shouldDisplay) {
            shouldDisplay = this.shouldDisplayTransformationOverride(process, parameters, dimensionName);
        }
        return shouldDisplay;
    }

    /**
     * Prompt the user for a name and create a new process.
     */
    static addProcess() {
        ComponentUtils.openNewEntityModal(
            'Process',
            (newName) => {
                const data =  _.cloneDeep(Constants.PROCESS_NEW);
                data.notification_email = ProcessUtils.getCurrentUserEmail();
                ProcessService.newProcess(newName, data);
            },
            new ProcessValidator(null).isNewProcessNameValid,
            { inputLabel: 'Name' }
        );
    }

    /**
     * Save as a new process
     * @param process object
     * @param revisionNum int
     */
    static saveAsNewProcess(process, revisionNum) {
        let titleName = process.name;
        if (revisionNum) {
            titleName += ` (rev ${revisionNum})`;
        }
        ComponentUtils.openNewEntityModal(
            'Process',
            (newName) => {
                ProcessService.saveAsNewProcess(process, revisionNum, newName);
            },
            new ProcessValidator(process).isNewProcessNameValid,
            {
                inputLabel: 'Name',
                modalTitle: `Save ${titleName} as a New Process`
            }
        );
    }

    static canOpenInWizardMode = (process) => {
        return ProcessUtils.hasVariableRetrieval(process) && !ProcessUtils.isAdvancedProcess(process);
    };

    static getWizardModeDisabledMessage = (process) => {
        let message = null;
        if (!ProcessUtils.hasVariableRetrieval(process)) {
            message =  'This process does not use ADI and is not suitable for the wizard.';

        } else if (ProcessUtils.isAdvancedProcess(process)) {
            message = `This process has advanced settings that may not be viewed or edited correctly 
                via the wizard.`;

        }

        return message;
    };

    static isAdvancedProcess = (process) => {

        const retrievedVariablesHasAdvanced = () => {
            let hasAdvancedSettings = false;
            const retrievedVariables = process.variable_retrieval.retrieved_variables;
            if (Object.keys(retrievedVariables).length > 0) {
                const firstVariable = retrievedVariables[Object.keys(retrievedVariables)[0]];

                const variableRequired = firstVariable.variable_required;
                const retrieveQC = firstVariable.retrieve_qc_variable;
                const qcRequired = firstVariable.qc_variable_required;

                ProcessUtils.visitRetrievedVariables(process, retrievedVariable => {
                    if (variableRequired !== retrievedVariable.variable_required ||
                        retrieveQC !== retrievedVariable.retrieve_qc_variable ||
                        qcRequired !== retrievedVariable.qc_variable_required) {
                        hasAdvancedSettings = true;
                    }

                    const primaryDod = ProcessUtils.getPrimaryDatastream(process, retrievedVariable.input_dataset);
                    const dodVar = ProcessUtils.getDatastreamVariable(primaryDod, retrievedVariable.name);
                    if (dodVar && dodVar.type !== retrievedVariable.data_type) {
                        hasAdvancedSettings = true;
                    }
                });
            }
            return hasAdvancedSettings;
        };

        const dimensionsHasAdvanced = () => {
            let hasAdvancedSettings = false;
            ProcessUtils.visitDimensions(process, dimension => {
                // set at runtime not available selection in process wizard
                if (dimension.gridding_method === Constants.COORDINATE_SYSTEM_GRIDDING_METHOD_SET_AT_RUNTIME) {
                    hasAdvancedSettings = true;
                }
            });
            return hasAdvancedSettings;
        };

        const inputDatasetsHasAdvanced = () => {
            let hasAdvancedSettings = false;
            const sgpDatastreamsWithMissingC1Data = Constants.SGP_DATASTREAMS_WITH_NO_C1_DATA;
            ProcessUtils.visitInputDatasets(process, inputDataset => {
                if (inputDataset.rules.length > 1) {
                    // Allow SGP E13 rule
                    const sgpC1 = new RunLocation('SGP', 'C1');
                    const sgpE13 = new RunLocation('SGP', 'E13');
                    const rule = inputDataset.rules[0];

                    if (inputDataset.rules.length === 2 && sgpDatastreamsWithMissingC1Data.indexOf(rule.datastream_name) > -1
                        && sgpC1.isEqual(rule.run_location) && sgpE13.isEqual(rule.data_location)) {
                        // this is allowed
                    } else {
                        hasAdvancedSettings = true;
                    }
                } else {
                    const rule = inputDataset.rules[0];

                    //check if input dataset and rule stray from default settings
                    if (!_.isEqual(inputDataset.offsets, Constants.INPUT_DATASET_NEW.offsets) ||
                        !_.isEqual(rule.run_location, Constants.INPUT_DATASET_RULE_NEW.run_location) ||
                        !_.isEqual(rule.run_time, Constants.INPUT_DATASET_RULE_NEW.run_time) ||
                        !_.isEqual(rule.data_location, Constants.INPUT_DATASET_RULE_NEW.data_location)
                    ) {
                        hasAdvancedSettings = true;
                    } else {
                        //check if alternate variable mappings were set
                        if (rule.variable_mappings.length > 1) {
                            hasAdvancedSettings = true;
                        }
                    }
                }
            });
            return hasAdvancedSettings;
        };

        const outputHasAdvanced = () => {
            let hasAdvancedSettings = false;
            const outputDatastream = process.output_datastreams[0];
            const outputMappings = process.variable_retrieval.output_datastream_variable_mappings[outputDatastream];
            if (outputMappings) {
                Object.keys(outputMappings).forEach(variableName => {
                    const defaultMapping = ProcessUtils.guessOutputDatastreamVariableName(process, outputDatastream, variableName);
                    if (outputMappings[variableName] !== defaultMapping) {
                        hasAdvancedSettings = true;
                    }
                });
            }
            return hasAdvancedSettings;
        };

        const transformationParametersHasAdvanced = () => {
            let hasAdvancedSettings = false;
            const wizardMode = ProcessUtils.isWizardMode(process.name);
            const exploredDatastreams = DataExplorerService.getExploredDatastreams(process.name);

            // Only perform this check if we are not currently in wizard mode, since newly created wizard
            // projects will cause this comparison to fail unless the wizard has been completely filled out
            if (!wizardMode && exploredDatastreams) {
                // If the user has modified the transformation parameters (specifically transformation type)
                // such that it violates the consistency checking rules set by the wizard, then the process is
                // considered to be in an advanced state.

                // Run fixProcessModel and compare the original process to see if there are any differences.
                const fixedProcess = ProcessUtils.fixProcessModel(_.cloneDeep(process), null, null, true);
                const originalCopyToDiff = JSON.parse(JSON.stringify(process));
                const fixedWizardCopyToDiff = JSON.parse(JSON.stringify(fixedProcess));

                if (!_.isEqual(fixedWizardCopyToDiff, originalCopyToDiff)) {
                    hasAdvancedSettings = true;
                }

            }
            return hasAdvancedSettings;
        };

        let advancedProcess = false;
        if (process) {

            if (!process.email_errors || process.email_warnings) {
                advancedProcess = true;

            } else if (ProcessUtils.hasVariableRetrieval(process)) {
                if (Object.keys(process.variable_retrieval.coordinate_systems).length > 1 ||
                    Object.keys(process.output_datastreams).length > 1 ||
                    retrievedVariablesHasAdvanced() ||
                    dimensionsHasAdvanced() ||
                    inputDatasetsHasAdvanced() ||
                    outputHasAdvanced() ||
                    transformationParametersHasAdvanced()
                ) {
                    advancedProcess = true;
                }
            }
        }

        return advancedProcess;
    };

    static getGlobalVariableProperty = (process, propertyName, defaultValue) => {
        let ret = defaultValue;
        if (process && process.variable_retrieval) {
            const varNames = Object.keys(process.variable_retrieval.retrieved_variables);
            if (varNames.length > 0) {
                // we can get the property value for the first variable, because all the vars will have the same value
                const variable = process.variable_retrieval.retrieved_variables[varNames[0]];
                ret = variable[propertyName];
            }
        }
        return ret;
    };

    static hasVariableRetrieval(process) {
        const retrieval = process.variable_retrieval;
        if (retrieval !== null && typeof retrieval !== 'undefined') {
            return true;
        }
        return false;
    }

    static isWizardMode(processName) {
        let wizardMode = false;
        const uiState = ProcessService.getMyState().processUIStateById[processName];
        if (uiState && uiState.mode === 'wizard') {
            wizardMode = true;
        }
        return wizardMode;
    }

    /**
     *
     * @param process
     * @param processUI
     * @param exploredDatastreams
     * @param forceWizardMode - only set this if you want to force a wizardMode fix
     * @returns {*}
     */
    static fixProcessModel(process, processUI, exploredDatastreams = null, forceWizardMode) {

        const editor = new ProcessEditor(process);

        // Fill empty default transform parameters
        if (!_.isEmpty(editor.process.variable_retrieval)) {
            if (exploredDatastreams) {
                exploredDatastreams = Object.assign({}, DataExplorerService.getExploredDatastreams(editor.process.name), exploredDatastreams);
            } else {
                exploredDatastreams = DataExplorerService.getExploredDatastreams(editor.process.name);
            }

            if (forceWizardMode || (processUI && processUI.mode === 'wizard' && this.canOpenInWizardMode(process))) {
                if (Object.keys(editor.process.variable_retrieval.retrieved_variables).length > 0) {

                    if (editor.process.output_datastreams.length === 0) {
                        const defaultOutputName = this.getDefaultOutputDatastreamName(editor.process);
                        editor.addOutputDatastream(defaultOutputName);
                    }

                    // Note that because this may add coordinate systems, we have to call fillEmptyTransformationParameters
                    // after this is called
                    if (Object.keys(editor.process.variable_retrieval.coordinate_systems).length === 0) {
                        editor.addCoordinateSystem(Constants.DEFAULT_COORDINATE_SYSTEM_NAME);
                    }
                }
                editor.fillEmptyTransformationParameters(exploredDatastreams);
                this.visitDimensions(editor.process, (dimension, coordinateSystemName) => {
                    editor.updateTransformationTypeDependentParameters(coordinateSystemName, dimension.name, exploredDatastreams);
                });


                if (Object.keys(editor.process.variable_retrieval.coordinate_systems).length > 0) {
                    this.setWizardDimensionDefaults(editor);
                }

            } else {
                editor.fillEmptyTransformationParameters(exploredDatastreams);
                this.visitDimensions(editor.process, (dimension, coordinateSystemName) => {
                    editor.updateTransformationTypeDependentParameters(coordinateSystemName, dimension.name, exploredDatastreams);
                });
            }

            // For backwards compatibility if there is an old trange value in a coordinate system dimension
            editor.convertTrange();
        }

        return editor.process;
    }

    //TODO: rename this since this does more than set defaults (e.g., fixDimensionSettings)
    static setWizardDimensionDefaults(editor) {
        this.visitDimensions(editor.process, (dimension, coordinateSystemName) => {
            const dimensionName = dimension.name;

            if (dimension.gridding_method === Constants.COORDINATE_SYSTEM_GRIDDING_METHOD_MAPPING && !dimension.input_dataset) {
                // If we have created a brand new dimension in the advanced editor, the defaults will be mapping with
                // no input dataset selected.  If we save these settings and open in wizard, we need to initialize
                // the input_dataset so it isn't null
                editor.initializeDimension(coordinateSystemName, dimensionName);
            }

            // We need this here because we may have multiple datasets for a non-time dimension, and if so,
            // we would need to change the non-time dimension to not be passthrough.
            // (Mainly this method only adjusts the default transformation type for non-time dimensions, so it should
            // probably be renamed.)
            // TODO: default transforamtion type bug ?
            editor.setDefaultDimensionTransformationType(coordinateSystemName, dimensionName);

            if (dimension.gridding_method === Constants.COORDINATE_SYSTEM_GRIDDING_METHOD_REGULAR_GRID) {
                const dt = dimension.data_transformation;
                if (dimensionName !== 'time' && dt.length === 0 && dt.start === 0 && dt.interval === 0 && dt.end === 0) {
                    // If the process has just been switched to regular grid and the defaults are all still set to 0,
                    // populate start/end based upon data explorer data

                    editor.initializeRegularGrid(coordinateSystemName, dimensionName);
                }

                // If we have a regular grid, we need to compute the length from the other 3 parameters
                editor.computeAndSetRegularGridLength(coordinateSystemName, dimensionName);

                // Make sure that if the value was previously mapping, there are no transform parameter overrides for this dimension
                editor.removePassthroughForDirectMappedVariables(coordinateSystemName, dimensionName);

            } else if (dimension.gridding_method === Constants.COORDINATE_SYSTEM_GRIDDING_METHOD_MAPPING) {
                // If we have a mapping, we have to override the transform type parameter for all variables that have
                // the same dataset and dimension to use passthrough.  This is because users assume that if they map
                // to a datstream, the variables coming from that same datastream should be exactly the same as the input.
                editor.setPassthroughForDirectMappedVariables(coordinateSystemName, dimensionName);
            }
        });
    }

    static hasTransformationParameter(process, parameterName) {
        let hasParameter = false;
        const transformParameters = process.variable_retrieval.transformation_parameters;
        if (transformParameters) {
            hasParameter = !!transformParameters[parameterName];
        }
        return hasParameter;
    }

    static getVariableTransformationTypeOverride(process, retrievedVariableName, dimensionName) {
        let ttype = null;

        if (process.variable_retrieval && process.variable_retrieval.transformation_parameters) {
            const ttypeParam = process.variable_retrieval.transformation_parameters.transformation_type;
            if (ttypeParam) {
                ttypeParam.variables.forEach(overrideVariable => {
                    if (overrideVariable.name === retrievedVariableName &&
                        overrideVariable.dim === dimensionName) {
                        ttype = overrideVariable.value;
                    }
                });
            }
        }
        return ttype;
    }

    static getVariableTransformationType(process, retrievedVariableName, dimensionName) {
        let ttype = this.getVariableTransformationTypeOverride(process, retrievedVariableName, dimensionName);

        if (!ttype) {
            const retrievedVariable = this.getRetrievedVariable(process, retrievedVariableName);
            if (retrievedVariable) {
                ttype = this.getDimensionTransformationType(process, retrievedVariable.coordinate_system, dimensionName);
            }
        }

        return ttype;
    }

    static getRetrievedVariable(process, retrievedVariableName) {
        let retrievedVariable = null;
        if (retrievedVariableName && process.variable_retrieval && process.variable_retrieval.retrieved_variables) {
            retrievedVariable = process.variable_retrieval.retrieved_variables[retrievedVariableName];
        }
        return retrievedVariable;
    }

    static getCoordinateSystem(process, coordinateSystemName) {
        let coordinateSystem = null;
        if (coordinateSystemName && process.variable_retrieval && process.variable_retrieval.coordinate_systems) {
            coordinateSystem = process.variable_retrieval.coordinate_systems[coordinateSystemName];
        }
        return coordinateSystem;
    }

    static getCoordinateSystemNames(process) {
        let coordinateSystems = [];
        if (process.variable_retrieval && process.variable_retrieval.coordinate_systems) {
            coordinateSystems = Object.keys(process.variable_retrieval.coordinate_systems);
        }
        return coordinateSystems;
    }

    static getCoordinateSystems(process) {
        let coordinateSystems = [];
        if (process.variable_retrieval && process.variable_retrieval.coordinate_systems) {
            coordinateSystems = Object.values(process.variable_retrieval.coordinate_systems);
        }
        return coordinateSystems;
    }

    static getDimension(process, coordinateSystemName, dimensionName) {
        let dim = null;
        if (coordinateSystemName &&
            dimensionName &&
            process.variable_retrieval &&
            process.variable_retrieval.coordinate_systems &&
            process.variable_retrieval.coordinate_systems[coordinateSystemName]) {
            dim = process.variable_retrieval.coordinate_systems[coordinateSystemName].dimensions[dimensionName];
        }
        return dim;
    }

    static getDimensionTransformationType(process, coordinateSystemName, dimensionName) {
        let value = null;
        if (coordinateSystemName && dimensionName) { // dimension might not be specified for some transform params

            const dimension = process.variable_retrieval.coordinate_systems[coordinateSystemName].dimensions[dimensionName];
            if (dimension && dimension.data_transformation) {
                value = dimension.data_transformation.transformation_type;
            }
        }
        return value;
    }

    static isInputQCParameter(parameterName) {
        return Constants.INPUT_QC_PARAMETERS.indexOf(parameterName) > -1;
    }

    //TODO: refactor to just remove these functions
    static isRelevantInputDatastreamOverrideVariable(process, parameterName, retrievedVariableName, exploredDatastreams) {
        return true;
    }

    static isRelevantInputDatastreamParameter(process, parameterName, exploredDatastreams) {
        // support selecting variabled not just found in data explorer
        return true;
    }

    static isRelevantCoordinateSystemParameter(process, parameterName, coordinateSystemNames, dimensionName) {
        let isRelevant = false;

        coordinateSystemNames.forEach(coordinateSystemName => {
            if (parameterName === 'transformation_type') {
                isRelevant = true;  // Transformation type is always relevant

            } else {
                const transformationTypeDefault = this.getDimensionTransformationType(process, coordinateSystemName, dimensionName);
                const variableTransformationTypes = {};

                // Get all the variables that apply to this dimension and coordinate system and assign the default transform
                ProcessUtils.visitRetrievedVariables(process, (retrievedVariable) => {
                    const coordSysMatch = retrievedVariable.coordinate_system === coordinateSystemName;
                    const dimMatch = retrievedVariable.dims.includes(dimensionName);
                    if (retrievedVariable.coordinate_system === coordinateSystemName && retrievedVariable.dims.includes(dimensionName)) {
                        variableTransformationTypes[retrievedVariable.name] = transformationTypeDefault;
                    }
                });

                // Now add any variable overrides from the transformation_type parameter
                ProcessUtils.visitTransformationVariables(process, 'transformation_type', (variableObj) => {
                    if (variableObj.name in variableTransformationTypes && variableObj.dim === dimensionName) {
                        variableTransformationTypes[variableObj.name] = variableObj.value;
                    }
                });

                // Find out if at least one variable in this coordinate system/dimension needs bin averaging
                // Find out if at least one variable in this coordinate system/dimension needs interpolation
                let hasAveraging = false;
                let hasInterpolation = false;
                let isPassthrough = true;
                Object.values(variableTransformationTypes).forEach(transformationType => {
                    if (transformationType === Constants.TRANSFORM_TYPE_AUTO ||
                        transformationType === Constants.TRANSFORM_TYPE_AVERAGE) {

                        hasAveraging = true;
                        isPassthrough = false;
                    }
                    if (transformationType !== Constants.TRANSFORM_TYPE_AVERAGE &&
                        transformationType !== Constants.TRANSFORM_TYPE_PASSTHROUGH) {
                        hasInterpolation = true;
                        isPassthrough = false;
                    }
                });

                if (!isPassthrough) {
                    switch (parameterName) {
                        case 'std_bad_max':
                        case 'std_ind_max':
                        case 'goodfrac_bad_min':
                        case 'goodfrac_ind_min':
                            if (hasAveraging) {
                                isRelevant = true;
                            }
                            break;
                        case 'range':
                            if (hasInterpolation) {
                                isRelevant = true;
                            }
                            break;
                        default:
                            isRelevant = true;
                            break;
                    }
                }
            }
        });

        return isRelevant;
    }

    /**
     * This method is used to determine if the transformation parameter is relevant to the process given its current state.
     * TODO: this method is confusing and so the logic will be hard to maintain.  We should rewrite it to use explicit
     * rules (i.e., if parameterName === 'missing_value' && missingValuesNotFoundForAllVariables) then...
     * @param process
     * @param parameterName
     * @param coordinateSystemName
     * @param dimensionName
     * @param exploredDatastreams
     * @returns {boolean}
     */
    static isRelevantTransformationParameter(process, parameterName, coordinateSystemNames, dimensionName, exploredDatastreams) {
        if (Constants.INPUT_QC_PARAMETERS.indexOf(parameterName) > -1) {
            return this.isRelevantInputDatastreamParameter(process, parameterName, exploredDatastreams);
        }
        return this.isRelevantCoordinateSystemParameter(process, parameterName, coordinateSystemNames, dimensionName);
    }

    static hasTransformationDefault(process, parameterNames, coordinateSystemName, dimensionName) {
        let hasDefault = false;
        let hasUIOverride = false;

        parameterNames.forEach(parameterName => {
            this.visitTransformationCoordinateSystemDefaults(process, parameterName, (coordinateObj) => {
                if (coordinateObj.name === coordinateSystemName && coordinateObj.dim === dimensionName && typeof coordinateObj.default_value !== 'undefined') {
                    hasDefault = true;
                }
            });

            const processUI = ProcessService.getMyState().processUIStateById[process.name];
            hasUIOverride = _.get(processUI, ['transformationParameterDefault', parameterName], false);
        });

        return hasDefault || hasUIOverride;
    }

    static getTransformationDefault(process, parameterName, coordinateSystemName, dimensionName) {
        let defaultValue = undefined;
        this.visitTransformationCoordinateSystemDefaults(process, parameterName, (coordinateObj) => {
            if (coordinateObj.name === coordinateSystemName && coordinateObj.dim === dimensionName) {
                defaultValue = coordinateObj.default_value;
            }
        });

        return defaultValue;
    }

    static shouldDisplayTransformationOverride(process, parameterNames, dimensionName) {
        let hasOverrideVariables = false;
        let hasUIOverride = false;

        parameterNames.forEach(parameterName => {
            // Input transformation parameters are not dimension specific, so if any are set, then it has overrides
            if (parameterName === 'qc_bad' || parameterName === 'qc_mask' || parameterName === 'missing_value') {
                hasOverrideVariables = process.variable_retrieval.transformation_parameters[parameterName].variables.length > 0;

            } else {
                // The other parameters are dimension specific, so we have to check for overrides just for that dimension
                this.visitTransformationVariables(process, parameterName, (variableObj) => {
                    if (variableObj.dim === dimensionName) {
                        hasOverrideVariables = true;
                    }
                });
            }

            // TODO: ui state needs to be coordinate system and dimension specific
            const processUI = ProcessService.getMyState().processUIStateById[process.name];
            hasUIOverride = _.get(processUI, ['transformationParameterOverride', parameterName], false);
        });

        return hasOverrideVariables || hasUIOverride;
    }

    static shouldDisplayDatastreamDefault(process, parameterNames) {
        let hasDatastreamDefault = false;
        let hasUIOverride = false;

        parameterNames.forEach(parameterName => {
            if (process.variable_retrieval.transformation_parameters[parameterName].input_datastream_defaults.length > 0) {
                hasDatastreamDefault = true;
            }


            // TODO: ui state needs to be coordinate system and dimension specific
            const processUI = ProcessService.getMyState().processUIStateById[process.name];
            hasUIOverride = _.get(processUI, ['transformationParameterDatastreamDefault', parameterName], false);
        });

        return hasDatastreamDefault || hasUIOverride;
    }

    static variableHasTransformationOverride(process, parameterName, retrievedVariableName) {
        let hasOverride = false;
        this.visitTransformationVariables(process, parameterName, (variableObj) => {
            if (variableObj.name === retrievedVariableName) {
                hasOverride = true;
            }
        });

        return hasOverride;
    }

    static visitTransformationParameters(process, visitFunction) {
        if (process.variable_retrieval) {
            const transformParameters = process.variable_retrieval.transformation_parameters;

            if (transformParameters) {
                Object.keys(transformParameters).forEach(parameterName => {
                    const parameter = transformParameters[parameterName];
                    visitFunction(parameter);
                });
            }
        }
    }

    static visitTransformationCoordinateSystemDefaults(process, parameterName, visitFunction) {
        if (process.variable_retrieval.transformation_parameters[parameterName] &&
            process.variable_retrieval.transformation_parameters[parameterName].coordinate_system_defaults
        ) {
            process.variable_retrieval.transformation_parameters[parameterName].coordinate_system_defaults.forEach(coordinateObj => {
                visitFunction(coordinateObj);
            });
        }
    }

    static visitTransformationInputDatastreamDefaults(process, parameterName, visitFunction) {
        if (process.variable_retrieval.transformation_parameters[parameterName] &&
            process.variable_retrieval.transformation_parameters[parameterName].input_datastream_defaults
        ) {
            process.variable_retrieval.transformation_parameters[parameterName].input_datastream_defaults.forEach(defaultObj => {
                visitFunction(defaultObj);
            });
        }
    }

    static visitTransformationVariables(process, parameterName, visitFunction) {
        if (process.variable_retrieval.transformation_parameters &&
            process.variable_retrieval.transformation_parameters[parameterName] &&
            process.variable_retrieval.transformation_parameters[parameterName].variables) {
            process.variable_retrieval.transformation_parameters[parameterName].variables.forEach(variableObj => {
                visitFunction(variableObj);
            });
        }
    }

    static mapTransformationCoordinateSystemDefaults(process, parameterName, mapFunction) {
        const coordinateDefaults = process.variable_retrieval.transformation_parameters[parameterName].coordinate_system_defaults;
        if (coordinateDefaults) {
            process.variable_retrieval.transformation_parameters[parameterName].coordinate_system_defaults = coordinateDefaults.map(coordinateObj => {
                return mapFunction(coordinateObj);
            });
        }

        return process;
    }

    static mapTransformationInputDatastreamDefaults(process, parameterName, mapFunction) {
        const inputDatastreamDefaults = process.variable_retrieval.transformation_parameters[parameterName].input_datastream_defaults;
        if (inputDatastreamDefaults) {
            process.variable_retrieval.transformation_parameters[parameterName].input_datastream_defaults = inputDatastreamDefaults.map(defaultObj => {
                return mapFunction(defaultObj);
            });
        }

        return process;
    }

    static mapTransformationVariables(process, parameterName, mapFunction) {
        const variables = process.variable_retrieval.transformation_parameters[parameterName].variables;
        if (variables) {
            process.variable_retrieval.transformation_parameters[parameterName].variables = variables.map(variableObj => {
                return mapFunction(variableObj);
            });
        }

        return process;
    }

    static filterTransformationCoordinateSystemDefaults(process, parameterName, filterFunction) {
        if (process.variable_retrieval.transformation_parameters && process.variable_retrieval.transformation_parameters[parameterName]) {
            const coordinateDefaults = process.variable_retrieval.transformation_parameters[parameterName].coordinate_system_defaults;
            if (coordinateDefaults) {
                process.variable_retrieval.transformation_parameters[parameterName].coordinate_system_defaults = coordinateDefaults.filter(coordinateObj => {
                    return filterFunction(coordinateObj);
                });
            }
        }

        return process;
    }

    static filterTransformationVariables(process, parameterName, filterFunction) {
        if (process.variable_retrieval.transformation_parameters && process.variable_retrieval.transformation_parameters[parameterName]) {
            const variables = process.variable_retrieval.transformation_parameters[parameterName].variables;
            if (variables) {
                process.variable_retrieval.transformation_parameters[parameterName].variables = variables.filter(variableObj => {
                    return filterFunction(variableObj);
                });
            }
        }

        return process;
    }

    static parameterIsExploredDataDependent(parameterName) {
        return typeof Constants.TRANSFORMATION_PARAMETERS[parameterName].explore_data_relevance !== 'undefined';
    }

    static parameterIsInputDatastreamDependent(parameterName) {
        return Constants.TRANSFORMATION_PARAMETERS[parameterName].file_type === Constants.FILE_TYPE_INPUT_DATASTREAM;
    }

    static parameterIsDimensionSpecific(parameterName) {
        return Constants.TRANSFORMATION_PARAMETERS[parameterName].dimension_specific;
    }

    static hasRelevantInputQCParameters(process, exploredDatastreams) {
        const parametersList = ['missing_value', 'qc_mask', 'qc_bad'];
        return this.hasRelevantTransformParameters(parametersList, process, exploredDatastreams);
    }

    static hasRelevantOutputQCParameters(process, exploredDatastreams) {
        const parametersList = ['std_ind_max', 'std_bad_max', 'goodfrac_ind_min', 'goodfrac_bad_min'];
        return this.hasRelevantTransformParameters(parametersList, process, exploredDatastreams);
    }

    static hasRelevantTransformParameters(parametersList, process, exploredDatastreams) {

        let hasRelevantParameters = false;

        Object.values(process.variable_retrieval.coordinate_systems).forEach(coordinateSystem => {
            Object.keys(coordinateSystem.dimensions).forEach(dimensionName => {
                if (!hasRelevantParameters) { // for performance, only do this loop if we haven't found relevancy yet
                    for (let i = 0; i < parametersList.length; i++) {
                        if (ProcessUtils.isRelevantTransformationParameter(process, parametersList[i], [coordinateSystem.name], dimensionName, exploredDatastreams)) {
                            hasRelevantParameters = true;
                            break;
                        }
                    }
                }
            });
        });
        return hasRelevantParameters;
    }

    static getVariablesWithRelevantExploredData(process, parameterName, exploredDatastreams) {

        // Path to field that must be set in explored data in order to include this variable
        const exploredDataPath = Constants.TRANSFORMATION_PARAMETERS[parameterName].explore_data_relevance;

        // Path to field that is used for variable default value in explored data
        const defaultValueDataPath = Constants.TRANSFORMATION_PARAMETERS[parameterName].explore_data_default_value;

        const getQcType = (exploredVariable) => {
            let qcType = null;
            if (exploredVariable.qc && exploredVariable.qc.type) {
                qcType = exploredVariable.qc.type;
            }
            return qcType;
        };

        const parameterVariables = {};
        process.input_datastreams.forEach(datastream => {
            if (exploredDatastreams[datastream] && !_.isEmpty(exploredDatastreams[datastream].currentData)) {
                const exploredDataVariables = exploredDatastreams[datastream].currentData.variables;

                Object.keys(exploredDataVariables).forEach(variableName => {
                    const variable = exploredDataVariables[variableName];

                    let value = _.get(variable, exploredDataPath);

                    let hasValue = false;
                    if (Array.isArray(value)) {
                        if (value.length > 0) {
                            hasValue = true;
                        }
                    } else if (value !== null) {
                        hasValue = true;
                    }

                    if (parameterName === 'qc_mask' && getQcType(variable) !== 'bit') {
                        hasValue = false;
                    } else if (parameterName === 'qc_bad' && getQcType(variable) !== 'integer') {
                        hasValue = false;
                    }

                    // include missing value parameter if it doesn't exist, all other parameters only include if they have a value
                    let addVariable = ((parameterName === 'missing_value' && !hasValue) || (parameterName !== 'missing_value' && hasValue));

                    // get retrieved variable name from input datastream variable name

                    // TODO this.getOriginalVariableNameFromInputstream wait this exists maybe switch?
                    let parameterRetrievedVariableName = variableName;
                    this.visitInputDatasets(process, inputDataset => {
                        this.visitInputDatasetRules(process, inputDataset.name, (rule) => {
                            if (rule.datastream_name === datastream) {
                                Object.keys(rule.variable_mappings).forEach(retrievedVariableName => {

                                    const mappings = rule.variable_mappings[retrievedVariableName];
                                    if (mappings) {
                                        if (mappings.indexOf(variableName) > -1) {
                                            parameterRetrievedVariableName = retrievedVariableName;
                                        }
                                    } else {
                                        console.log('no mappings');
                                    }
                                });
                            }
                        });
                    });

                    let dims = [];
                    if (process.variable_retrieval.retrieved_variables[parameterRetrievedVariableName]) {
                        dims = process.variable_retrieval.retrieved_variables[parameterRetrievedVariableName].dims;
                    }

                    // don't add if variable isn't retrieved
                    if (Object.keys(process.variable_retrieval.retrieved_variables).indexOf(parameterRetrievedVariableName) === -1) {
                        addVariable = false;

                    } else if (dims && dims.length === 0) {
                        addVariable = false; // do not include static variables as they will never be transformed

                    } else if (addVariable && parameterName !== 'transformation_type') {

                        // Don't add if the variable is passthrough, since these parameters won't apply
                        // We have to check every dimension for that variable
                        let isPassthroughVariable = false;

                        if (dims) {
                            dims.forEach(dim => {
                                const variableTType = ProcessUtils.getVariableTransformationType(process, parameterRetrievedVariableName, dim);
                                if (variableTType === Constants.TRANSFORM_TYPE_PASSTHROUGH) {
                                    isPassthroughVariable = true;
                                }
                            });
                        }
                        addVariable = !isPassthroughVariable;
                    }

                    if (addVariable) {
                        if (!parameterVariables[datastream]) {
                            parameterVariables[datastream] = {};
                        }

                        if (Constants.TRANSFORMATION_PARAMETERS[parameterName].default_value) {
                            value = Constants.TRANSFORMATION_PARAMETERS[parameterName].default_value;

                        } else {
                            value = _.get(variable, defaultValueDataPath);
                        }

                        parameterVariables[datastream][parameterRetrievedVariableName] = value;
                    }
                });
            }
        });

        return parameterVariables;
    }

    static getQCBits(process, datastreamName, retrievedVariableName, bitAssessmentType, qcType) {
        const inputVariableName = this.getOriginalVariableNameFromInputstream(process, retrievedVariableName, datastreamName);
        const explorerData = DataExplorerService.getCurrentData(process.name, datastreamName);
        const key = bitAssessmentType === 'bad' ? 'bad_values' : 'indeterminate_values';
        const variableQCType = _.get(explorerData, ['variables', inputVariableName, 'qc', 'type'], null);
        let values = [];

        if (variableQCType === qcType) {
            values = _.get(explorerData, ['variables', inputVariableName, 'qc', key], []);
        }

        return values;
    }

    static getQCDescriptions(process, datastreamName, retrievedVariableName) {
        const inputVariableName = this.getOriginalVariableNameFromInputstream(process, retrievedVariableName, datastreamName);
        const explorerData = DataExplorerService.getCurrentData(process.name, datastreamName);

        return _.get(explorerData, ['variables', inputVariableName, 'qc', 'descriptions']);
    }

    /** for the number list input fields used in qc mask and qc bad **/
    static getQCInputValue(value) {
        // QC inputs need to convert comma or space delimited string back to an array
        //TODO: this will need more validation going on that's for sure.
        value = value.split(/[ ,]+/).reduce((acc, val) => {
            val = val.trim();
            if (val && !isNaN(val) && acc.indexOf(val) === -1) {
                acc.push(parseInt(val.trim(), 10));
            }
            return acc;
        }, []);

        return value;
    }

    static convertBitPositionsToInteger(bitPositions) {
        if (bitPositions !== null && bitPositions.length > 0) {
            let binaryString = '00000000000000000000000000000000';

            const substr_replace = function (str, replace, start, length) {

                if (start < 0) {
                    // start position in str
                    start = start + str.length;
                }
                length = length !== undefined ? length : str.length;
                if (length < 0) {
                    length = length + str.length - start;
                }

                return [
                    str.slice(0, start),
                    replace.substr(0, length),
                    replace.slice(length),
                    str.slice(start + length)
                ].join('');
            };

            bitPositions.forEach(bitPosition => {
                // update the character at positon ($binaryString.length - $bitPosition)
                // i.e., 00000000000000000000000000000111
                const charPosition = binaryString.length - parseInt(bitPosition);
                binaryString = substr_replace(binaryString, '1', charPosition, 1);
            });

            return parseInt(binaryString, 2);
        }
    }

    static getTransformOverrideVariableFieldPath(parameterName, coordinateSystemName, dimensionName, inputDatastream, variableName) {
        let fieldPath = ['variable_retrieval', 'transformation_parameters', parameterName, 'coordinate_system_defaults'];
        if (coordinateSystemName) {
            fieldPath = [...fieldPath, coordinateSystemName, dimensionName];
        }
        if (inputDatastream) {
            fieldPath = [...fieldPath, inputDatastream];
        }

        return [...fieldPath, variableName];
    }

    static hasDataExplorerParameters(process) {
        return this.hasRunLocations(process);
    }

    static callDataExplorerWebService(process, datastreamsToExplore) {

        // Trigger web services
        const datastreamExploreRequests = [];
        Object.keys(datastreamsToExplore).forEach(inputDatastreamName => {
            const searchFolders = ProcessUtils.getSearchFoldersForExplorer(process, inputDatastreamName);
            const variables = Object.keys(datastreamsToExplore[inputDatastreamName]);
            const currentData = DataExplorerService.getCurrentData(process.name, inputDatastreamName);
            const ignoreFiles = currentData.exploredFiles || []; // ignore files that were already explored

            datastreamExploreRequests.push({
                datastreamName: inputDatastreamName,
                variables,
                ignoreFiles,
                searchFolders
            });
        });
        DataExplorerService.exploreDatastreams(process.name, process, datastreamExploreRequests);

    }

    static getDimensionInterval(process, coordinateSystemName, dimensionName, exploredDatastreams) {
        let value = null;
        const dimension = process.variable_retrieval.coordinate_systems[coordinateSystemName].dimensions[dimensionName];

        if (dimension.gridding_method === Constants.COORDINATE_SYSTEM_GRIDDING_METHOD_REGULAR_GRID) {
            value = dimension.data_transformation.interval;

        } else if (dimension.gridding_method === Constants.COORDINATE_SYSTEM_GRIDDING_METHOD_MAPPING) {
            const datastreamName = this.getPrimaryDatastream(process, dimension.input_dataset);

            //dimension may have null input datastream if deleted in main pcm
            const exploredData = exploredDatastreams && datastreamName ?
                exploredDatastreams[datastreamName] : DataExplorerService.getExploredDatastream(process.name, datastreamName);

            if (exploredData) {
                const dimData = _.get(exploredData, ['currentData', 'dimensions', dimensionName], null);
                if (dimData) {
                    const median = dimData.median_interval;

                    if (median) {
                        value = median;
                    }
                }
            }
        }

        return value;
    }

    static newRetrievedVariableFunction(process) {
        return () => {
            ComponentUtils.openImportWizardModal(process.name);
        };
    }

    static getCurrentUserFullName() {

        const state = SecurityService.getMyState();
        const currentUser = state.username;
        let name = currentUser;
        if (state.users) {
            const user = state.users[currentUser];
            if (user && user.firstName && user.lastName) {
                name = `${user.firstName} ${user.lastName}`;
            }
        }
        return name;
    }

    static getCurrentUserEmail() {
        let email = '';
        const state = SecurityService.getMyState();
        const currentUser = state.username;
        const user = state.users[currentUser];
        if (user) {
            email = user.email;
        }
        return email;
    }

    static getCurrentUsername() {
        const state = SecurityService.getMyState();
        const currentUser = state.username;
        return currentUser;
    }

    static canCurrentUserEditProcess(processId, deprecated) {
        return SecurityService.canCurrentUserEditProcess(processId) && !deprecated;
    }

    static newOutputDatastreamFunction(process, retrievedVariable, navigate) {
        const dods = DodService.getMyState(DodService.getStore().getState()).sortedIds;
        const isOutputDatastreamNameValid = (name) => {
            return new ProcessValidator(process).isDatastreamNameValid(name, process.output_datastreams);
        };

        return () => {
            ComponentUtils.openNewEntityModal('Output Datastream',
                (newName) => {
                    const editor = new ProcessEditor(process);
                    editor.addOutputDatastream(newName);
                    if (retrievedVariable) {
                        editor.addOutputDatastreamToVariable(newName, retrievedVariable);
                    }
                    editor.commit();

                    // We have to make sure the variables for the datastream are loaded in the cache
                    DodService.fetchDodVarsIfNeeded(newName);

                    if (navigate) {
                        NavigationUtils.gotoOutputDatastream(process.name, null, newName);
                    }
                },
                isOutputDatastreamNameValid,
                {
                    options: Constants.convertArrayToSelectOptions(dods),
                    inputLabel: 'New Datastream Name',
                    newSelectionPrefix: "New (enter 'classname.level'): "
                });
        };
    }

    /**
     * Look up the set of datastream coordinate variables for the given dimension.  If a datastreamName
     * is specified, then only return the units from the coordinate variable for that specific datastream.
     * Otherwise, return the coordinate variable units from all datastreams with this dimension.
     * defined for this process.
     * @param {Object} process
     * @param {string} dimensionName
     * @param {string} datastreamName (optional)
     */
    static getDimensionCoordinateVariableUnits(process, dimensionName, datastreamName) {
        const units = [];
        const datastreams = [...process.input_datastreams]; // do we add output datastreams too?
        datastreams.forEach(dsName => {
            const cVars = ProcessUtils.getDatastreamVariable(dsName, 'cVars');
            if (_.isObject(cVars)) {

                Object.keys(cVars).forEach(coordinateVariableName => {
                    const coordinateVariable = cVars[coordinateVariableName];
                    // Only add if the variable has the same dimension as this dimension
                    // TODO: look somewhere to see if this dimension has been renamed and if so, we
                    // also search for cVar dims of the old name (where should we save the old name?)
                    if (coordinateVariable.dims && coordinateVariable.dims.indexOf(dimensionName) !== -1) {
                        // Filter if a specific datastreamName is requested
                        if (!datastreamName || datastreamName === dsName) {
                            // Only add if it already isn't already in the list
                            if (coordinateVariable.units && units.indexOf(coordinateVariable.units) === -1) {
                                units.push(coordinateVariable.units);
                            }
                        }
                    }
                });
            }
        });
        return units;
    }

    static newCoordinateSystemFunction(process, retrievedVariable, navigate) {
        const isCoordinateSystemNameValid = (name) => {
            const existingList = Object.keys(process.variable_retrieval.coordinate_systems);
            return new ProcessValidator(process).isEntityNameValid(name, existingList, 'Coordinate system');
        };

        return () => {
            ComponentUtils.openNewEntityModal('Coordinate System',
                (coordinateSystemName) => {
                    const editor = new ProcessEditor(process);
                    editor.addCoordinateSystem(coordinateSystemName);
                    if (retrievedVariable) {
                        editor.setCoordinateSystemForVariable(coordinateSystemName, retrievedVariable);
                    }
                    editor.commit();
                    if (navigate) {
                        NavigationUtils.gotoCoordinateSystem(process.name, null, coordinateSystemName);
                    }
                },
                isCoordinateSystemNameValid, { inputLabel: 'New Name' });
        };
    }

    static newInputDatasetRetrievalRuleFunction(process, inputDatasetName) {
        const dods = DodService.getMyState(DodService.getStore().getState()).sortedIds;
        const isInputValid = (datastreamName) => {
            const validator = new ProcessValidator(process);
            let error = validator.isEntityNull(datastreamName, 'Datastream');
            if (error.level === null) {
                error = validator.isDatastreamNameValid(datastreamName);
            }

            return error;
        };

        // Recursive function that keeps timing out until the datastream variables have been loaded into the cache.
        // We need the datastream variables loaded in order to properly guess the mappings!
        const finishWhenDatastreamLoaded = datastream => {
            const datastreamVariables = DodService.getMyState(DodService.getStore().getState()).dodsById[datastream];
            if (datastreamVariables === null || typeof(datastreamVariables) === 'undefined') {
                setTimeout(() => {
                    finishWhenDatastreamLoaded(datastream);
                }, 100);

            } else {
                const editor = new ProcessEditor(process);
                const newRule = _.cloneDeep(Constants.INPUT_DATASET_RULE_NEW);
                newRule.datastream_name = datastream;

                // Now guess the variable mappings for the new rule
                const mappings = {};
                const retrievedVariablesForDataset = ProcessUtils.getRetrievedVariablesForDataset(process,
                    inputDatasetName);
                retrievedVariablesForDataset.forEach(assignedVariableName => {
                    const datastreamVariableNames = this.guessInputDatastreamVariableNames(process, inputDatasetName,
                        assignedVariableName, datastream);
                    mappings[assignedVariableName] = datastreamVariableNames;
                });
                newRule.variable_mappings = mappings;

                editor.editInputDataset(inputDatasetName, null, newRule);
                //editor.mergeInputDatasets();
                editor.commit();
            }
        };

        return () => {
            ComponentUtils.openNewEntityModal('Retrieval Rule',
                (datastream) => {
                    // We have to make sure the variables for the datastream are loaded in the cache
                    DodService.fetchDodVarsIfNeeded(datastream);

                    // Wait until the variables are loaded before completing the transaction
                    finishWhenDatastreamLoaded(datastream);
                },
                isInputValid,
                {
                    options: Constants.convertArrayToSelectOptions(dods),
                    inputLabel: 'Retrieve from datastream',
                    multi: false,
                    creatable: true
                });
        };
    }

    /**
     * For the given assigned variable, guess the best datastream variable name(s) for the given datastream
     * @param process
     * @param datasetName
     * @param assignedVariableName
     * @param datastreamName
     */
    static guessInputDatastreamVariableNames(process, datasetName, assignedVariableName, datastreamName) {
        let datastreamVariableNames = [];
        const dodVars = ProcessUtils.getDatastreamVariablesForDod(datastreamName);

        // First check if the assignedVariableName is in the datastream
        if (ProcessUtils.isVariableInDatastream(datastreamName, assignedVariableName)) {
            datastreamVariableNames.push(assignedVariableName);

        } else {
            // Then walk through all the rules, getting the variable mappings for that assignedVariable name
            // See if one of the mapped names is present in the datastream
            const dataset = process.variable_retrieval.input_datasets[datasetName];
            dataset.rules.forEach(rule => {
                const ruleMappedVariableNames = rule.variable_mappings[assignedVariableName];
                ruleMappedVariableNames.forEach(ruleMappedVariableName => {
                    if (ruleMappedVariableName && ProcessUtils.isVariableInDatastream(datastreamName, ruleMappedVariableName)) {
                        // add if it isn't already in the list
                        if (!_.includes(datastreamVariableNames, ruleMappedVariableName)) {
                            datastreamVariableNames.push(ruleMappedVariableName);
                        }
                    }
                });
            });
        }

        if (datastreamVariableNames.length === 0) {
            // Default value is assignedVariableName
            datastreamVariableNames.push(assignedVariableName);
        } else {
            // add prior names after in mappings list
            datastreamVariableNames.forEach(variableName => {
                if (dodVars[variableName]) {
                    const priorNames = dodVars[variableName].renamed;
                    if (priorNames && priorNames.length > 0) {
                        datastreamVariableNames.push(...priorNames);
                    }
                }
            });
        }

        return datastreamVariableNames;
    }

    /**
     * For the given retrieved(assigned) variable, guess the variable name to use for the output datastream mapping.
     * Returns null if a good option can't be found.
     * @param process - process object
     * @param datastreamName - name of output datastream
     * @param assignedVariableName - name of retrieved variable
     */
    static guessOutputDatastreamVariableName(process, datastreamName, assignedVariableName) {
        let datastreamVariableName = null;
        const datastreamVariables = this.getDatastreamVariablesForDod(datastreamName);

        if (!datastreamVariables) {
            // This is a DOD on the fly, so we will use the assigned name as the datastream variable name
            datastreamVariableName = assignedVariableName;

        } else if (Object.keys(datastreamVariables).length === 0) {
            // This is a DOD on the fly, so we will use the assigned name as the datastream variable name
            datastreamVariableName = assignedVariableName;

        } else if (datastreamVariables[assignedVariableName]) {
            // There is a variable in the datastream that matches the assigned name, so use it
            datastreamVariableName = assignedVariableName;

        } else {
            // If we can't find a mapping to the assigned name, then we need to check to see if there is
            // an input variable mapping that matches an output datastream variable
            const retrievedVariable = process.variable_retrieval.retrieved_variables[assignedVariableName];
            const inputDataset = process.variable_retrieval.input_datasets[retrievedVariable.input_dataset];
            inputDataset.rules.forEach(rule => {
                const mappings = rule.variable_mappings[assignedVariableName];
                if (mappings) {
                    mappings.forEach(inputDatastreamVariableName => {
                        if (this.isVariableInDatastream(datastreamName, inputDatastreamVariableName)) {
                            datastreamVariableName = inputDatastreamVariableName;
                        }
                    });
                }
            });
        }
        return datastreamVariableName;
    }

    /**
     * Find any datasets which have datastreams with a non-time dimension.  Used to populate the coordinate system
     * mapping dropdown list.
     * @param process
     * @returns {string[]}
     */
    static getDatasetsWithNonTimeDimension(process) {
        const inputDatasets = {};
        this.visitRetrievedVariables(process, (retrievedVariable) => {
            const inputDataset = process.variable_retrieval.input_datasets[retrievedVariable.input_dataset];
            const dims = retrievedVariable.dims;
            if (dims.length > 1 || (dims.length === 1 && dims[0] !== 'time')) {
                inputDatasets[inputDataset.name] = true;
            }
        });
        return Object.keys(inputDatasets);
    }

    /**
     * This returns a set of input dataset names for retrieved variables that are in the given coordinate system and
     * dimension.  This is used to warn users if their coordinate system is fed by multiple datasets but they
     * have selected passthrough as the transformation type.
     * @param process
     * @param coordinateSystemName
     * @param dimensionName
     * @returns {string[]}
     */
    static getInputDatasetsForCoordSysDimension(process, coordinateSystemName, dimensionName) {
        const inputDatasets = {};
        this.visitRetrievedVariables(process, (retrievedVariable) => {
            const inputDataset = process.variable_retrieval.input_datasets[retrievedVariable.input_dataset];

            if (retrievedVariable.coordinate_system === coordinateSystemName &&
                retrievedVariable.dims.indexOf(dimensionName) > -1) {
                inputDatasets[inputDataset.name] = true;
            }
        });

        return Object.keys(inputDatasets);
    }

    /**
     * This returns any input dataset that has a variable that has the given dimension name.  This is used
     *  to identify datasets that are a valid choice to assign to the mapping shape of a coordinate system dimension.
     * @param process
     * @param dimensionName
     * @returns {string[]}
     */
    static getInputDatasetsForDimension(process, dimensionName) {
        const inputDatasets = {};
        this.visitRetrievedVariables(process, (retrievedVariable) => {
            const inputDataset = process.variable_retrieval.input_datasets[retrievedVariable.input_dataset];
            const datastreamName = inputDataset.rules[0].datastream_name;
            const datastreamVariableName = inputDataset.rules[0].variable_mappings[retrievedVariable.name];

            const dodVar = ProcessUtils.getDatastreamVariable(datastreamName, datastreamVariableName);
            if (dodVar) {
                if (dodVar.dims.indexOf(dimensionName) > -1 || retrievedVariable.dims.indexOf(dimensionName) > -1) {
                    inputDatasets[inputDataset.name] = true;
                }
            }
        });

        return Object.keys(inputDatasets);
    }

    static getInputDatasetForVariable(process, variableName) {
        let inputDataset = null;
        if (process.variable_retrieval && process.variable_retrieval.retrieved_variables && process.variable_retrieval.retrieved_variables[variableName]) {
            inputDataset = process.variable_retrieval.retrieved_variables[variableName].input_dataset;
        }
        return inputDataset;
    }

    static getDimensionsAsArray(process, coordinateSystemName) {
        const dimsArray = [];
        Object.values(process.variable_retrieval.coordinate_systems[coordinateSystemName].dimensions).forEach(dimension => {
            dimsArray[dimension.index] = dimension;
        });
        return dimsArray;
    }

    static getDimensionNamesAsArray(process, coordinateSystemName) {
        const dimsArray = [];
        if (process.variable_retrieval.coordinate_systems[coordinateSystemName]) {
            if (process.variable_retrieval.coordinate_systems[coordinateSystemName].dimensions) {
                Object.values(process.variable_retrieval.coordinate_systems[coordinateSystemName].dimensions).forEach(dimension => {
                    dimsArray[dimension.index] = dimension.name;
                });
            }
        }
        return dimsArray;
    }

    static getNonPassthroughDimensionNames(process, coordinateSystemName) {
        const dimensions = this.getDimensionsAsArray(process, coordinateSystemName);
        return dimensions.filter(dimension => {
            return dimension.data_transformation.transformation_type !== Constants.TRANSFORM_TYPE_PASSTHROUGH;
        }).map(dimension => {
            return dimension.name;
        });
    }

    /**
     * Returns true if the given variable name exists in the datastream.
     * @param datastreamName
     * @param variableName
     */
    static isVariableInDatastream(datastreamName, variableName) {
        const datastreamVariable = ProcessUtils.getDatastreamVariable(datastreamName, variableName);
        return datastreamVariable !== null;
    }

    /**
     * Gets the given variable from the given datastream.
     * Returns null if the datastream or the variable does not exist.
     * @param datastreamName
     * @param variableName
     * @returns {Object|null}
     */
    static getDatastreamVariable(datastreamName, variableName) {
        const dodsById = DodService.getMyState(DodService.getStore().getState()).dodsById;
        let datastreamVariable = null;
        if (dodsById[datastreamName]) {
            if (dodsById[datastreamName][variableName]) {
                datastreamVariable = dodsById[datastreamName][variableName];
            }
        }

        return datastreamVariable;
    }

    /**
     * Coordinate variables are stored separately from the other variables.
     * @param datastreamName
     * @param coordinateVariableName
     * @returns {*}
     */
    static getDatastreamCoordinateVariable(datastreamName, coordinateVariableName) {
        const dodsById = DodService.getMyState(DodService.getStore().getState()).dodsById;
        let datastreamCoordinateVariable = null;
        const dodVars = dodsById[datastreamName];
        if (dodVars) {
            if (dodVars.cVars) {
                // First add the coordinate variables to the top of the list
                Object.keys(dodVars.cVars).forEach(variableName => {
                    if (variableName === coordinateVariableName) {
                        datastreamCoordinateVariable = variableName;
                    }
                });
            }
        }
        return datastreamCoordinateVariable;
    }

    static getCoordinateVariableOptionsForDatastream(datastreamId) {
        const dodsById = DodService.getMyState(DodService.getStore().getState()).dodsById;
        const dodVars = dodsById[datastreamId];
        const variableOptions = [];

        if (dodVars) {
            if (dodVars.cVars) {
                // First add the coordinate variables to the top of the list
                Object.keys(dodVars.cVars).forEach(variableName => {
                    variableOptions.push({ value: variableName, label: variableName });
                });

                // Then add the rest of the datastream variables
                Object.keys(dodVars).forEach(variableName => {
                    if (variableName !== 'cVars') {
                        variableOptions.push({ value: variableName, label: variableName });
                    }
                });
            }
        }
        return variableOptions;
    }

    static getDatasetsWithGeospatialVariables(process) {
        const datasetNames = [];

        this.visitInputDatasets(process, inputDataset => {
            // make sure that all datastreams used in the dataset have alt/lat/lon
            let datasetHasGeospatialVariables = true;

            this.visitInputDatasetRules(process, inputDataset.name, (rule) => {
                const datastreamName = rule.datastream_name;
                const datastreamVariables = DodService.getMyState().dodsById[datastreamName];
                let hasAlt = false;
                let hasLat = false;
                let hasLon = false;

                Object.keys(datastreamVariables).forEach((variableName) => {
                    if (variableName === 'alt') {
                        hasAlt = true;
                    } else if (variableName === 'lat') {
                        hasLat = true;
                    } else if (variableName === 'lon') {
                        hasLon = true;
                    }
                });
                const ruleHasGeospatialVariables = hasAlt && hasLat && hasLon;
                datasetHasGeospatialVariables &= ruleHasGeospatialVariables;
            });

            if (datasetHasGeospatialVariables) {
                datasetNames.push(inputDataset.name);
            }
        });
        return datasetNames;
    }

    static hasGeospatialVariables(process) {
        let hasAlt = false;
        let hasLat = false;
        let hasLon = false;
        this.visitRetrievedVariables(process, retrievedVariable => {
            if (retrievedVariable.name === 'alt') {
                hasAlt = true;
            } else if (retrievedVariable.name === 'lat') {
                hasLat = true;
            } else if (retrievedVariable.name === 'lon') {
                hasLon = true;
            }
        });
        return hasAlt && hasLat && hasLon;
    }

    static hasRunLocations(process) {
        return process.run_locations && process.run_locations.length > 0;
    }

    static getIgnoredValidation(processName) {
        const currentUser = ProcessUtils.getCurrentUsername();
        const process = ProcessService.getMyState().workingCopiesById[processName];

        if (process && process.ui_state && process.ui_state.ignore_validation && process.ui_state.ignore_validation[currentUser]) {
            return process.ui_state.ignore_validation[currentUser];
        }
        return null;
    }

    static hasDataFolders(process) {
        let ret = false;
        if (process.ui_state && process.ui_state.data_locations) {
            const dataLocations = process.ui_state.data_locations;
            if (dataLocations.use_user || dataLocations.use_archive || dataLocations.use_test) {
                ret = true;
            }
        }
        return ret;
    }

    /**
     * Load the run parameters from the given process.  This is called the first time the run data consolidator
     * dialog is shown to populate the UI.
     * @param process
     */
    static getRunParameters(process, rangeStatus) {
        let runParameters = _.cloneDeep(_.get(process, 'ui_state.run_parameters', {}));

        // We are storing data locations outside the run parameters because it is also used for data explorer
        let dataLocations = ProcessUtils.getDataLocations(process);
        if (!dataLocations) {
            dataLocations = _.cloneDeep(Constants.DATA_LOCATIONS_NEW);
        }
        runParameters.data_locations = dataLocations;

        // Backwards compatibility since we moved old run parameters under ui_state.run_parameters
        if (_.isNil(runParameters.start_date)) {
            runParameters.start_date = process.ui_state.start_date || '';
        }
        if (_.isNil(runParameters.end_date)) {
            runParameters.end_date = process.ui_state.end_date || '';
        }
        if (_.isNil(runParameters.run_locations)) {
            runParameters.run_locations = process.ui_state.run_locations || null;
        }

        if (!runParameters.run_locations || runParameters.run_locations.length === 0) {
            if (process.run_locations.length > 0) {
                runParameters.run_locations = [_.cloneDeep(process.run_locations[0])];
            }
        }

        // Always change the email to the current user's email
        runParameters.contact_email = ProcessUtils.getCurrentUserEmail();

        // See if we should add an overridable time_range parameter
        if (rangeStatus === 'overridable') {
            runParameters.time_range = 1800;
        } else {
            delete runParameters.time_range;
        }

        // For backwards compatibility, we need to make sure new parameters are added
        if (_.isNil(runParameters.debug_level)) {
            runParameters = Object.assign({
                debug_level: '3',
                include_debug_dumps: false,
                include_transformation_metrics: false,
                include_glattr_vars: false,
                include_qc_vars: true,
                include_dqr_assessments: true,
                rollup_qc: true,
                missing_values_disposition: 'SET_BAD_MISSING'
            }, runParameters);
        } else if (runParameters.debug_level == '0') {
            runParameters.debug_level = '3';
        }
        return runParameters;
    }

    static getDataLocations(process) {
        let ret = null;
        if (process.ui_state) {
            ret = process.ui_state.data_locations;
        }
        return ret;
    }

    static getSearchFoldersForExplorer(process, inputDatastreamName) {
        let searchFolders = [];

        if (process.ui_state && process.ui_state.data_locations) {
            const dataLocations = process.ui_state.data_locations;
            if (dataLocations.use_user) {
                searchFolders = searchFolders.concat(ProcessUtils.getSearchFolderPaths(inputDatastreamName, process.run_locations, dataLocations.user_folder));
            }
            if (dataLocations.use_archive) {
                searchFolders = searchFolders.concat(ProcessUtils.getSearchFolderPaths(inputDatastreamName, process.run_locations, ProcessUtils.getArchiveFolder()));
            }
            if (dataLocations.use_test) {
                searchFolders = searchFolders.concat(ProcessUtils.getSearchFolderPaths(inputDatastreamName, process.run_locations, ProcessUtils.getProcessTestFolder(process)));
            }

        } else {
            // use the archive if not previously specified.
            searchFolders = searchFolders.concat(ProcessUtils.getSearchFolderPaths(inputDatastreamName, process.run_locations, ProcessUtils.getArchiveFolder()));
        }
        return searchFolders;
    }

    static getSearchFolderPaths(inputDatastreamName, runLocations, basePath) {
        /*
         "run_locations": [
         {
         "site": "NSA",
         "fac": "C1"
         }
         ],
         */

        const searchFolderPaths = [];
        const parts = inputDatastreamName.split('.');
        const className = parts[0];
        const dataLevel = parts[1];
        const sgpC1 = new RunLocation('SGP', 'C1');
        const sgpE13 = new RunLocation('SGP', 'E13');
        let needsE13 = false;
        let hasE13 = false;

        runLocations.forEach(runLocation => {

            // i.e., basePath + /sgp/sgpvisstpx04g10v2minnisX1.c1
            const site = runLocation.site.toLowerCase();
            const fac = runLocation.fac.toUpperCase();
            const path = `${basePath}/${site}/${site}${className}${fac}.${dataLevel}`;
            searchFolderPaths.push(path);

            if (sgpE13.isEqual(runLocation)) {
                hasE13 = true;
            }
            if (sgpC1.isEqual(runLocation)) {
                needsE13 = true;
            }
        });

        const sgpDatastreamsWithMissingC1Data = Constants.SGP_DATASTREAMS_WITH_NO_C1_DATA;

        if (sgpDatastreamsWithMissingC1Data.indexOf(inputDatastreamName) > -1 && needsE13 && !hasE13) {
            const site = sgpE13.site.toLowerCase();
            const fac = sgpE13.fac.toUpperCase();
            const path = `${basePath}/${site}/${site}${className}${fac}.${dataLevel}`;
            searchFolderPaths.push(path);
        }

        return searchFolderPaths;
    }

    static getProcessTestFolder(process) {
        return `/data/home/dev/vap/${process.name}/DATA/data/datastream`;
    }

    static getProcessDefaultUserFolder() {
        const user = ProcessUtils.getCurrentUsername();
        return `/data/home/${user}/data/datastream`;
    }

    static getArchiveFolder() {
        return '/data/archive';
    }

    static hasRunLocation(process, runLocation) {
        let match = false;
        process.run_locations.forEach(rl => {
            if (runLocation.site === rl.site && runLocation.fac === rl.fac) {
                match = true;
            }
        });
        return match;
    }

    /**
     * Compute the list of run locations used by the given process
     * @returns string[]
     */
    static getProcessRunLocations(process) {
        return process.run_locations.reduce((accumulator, runLocation) => {
            if (runLocation.site && runLocation.fac) {
                accumulator.push(`${runLocation.site.toUpperCase()} ${runLocation.fac.toUpperCase()}`);
            }
            return accumulator;
        }, []);
    }

    static getRunLocationOptions(siteFacilities) {
        let options = siteFacilities ? siteFacilities : [];
        options = options.reduce((accumulator, currentValue) => {
            // If value doesn't contain a space (e.g., SGP) then we throw it out because for run locations, we must
            // define both a site and a facility
            if (currentValue.indexOf(' ') >= 0) {
                accumulator.push(currentValue);
            }
            return accumulator;
        }, []);

        options = Constants.convertArrayToSelectOptions(options);
        return options;
    }

    static getRuleRunLocationOptions(process) {
        const Set = require('es6-set');
        const sites = new Set();
        let runLocations = [];

        process.run_locations.forEach(runLocation => {
            let runLocationString = runLocation.site;
            if (runLocation.fac) {
                runLocationString += ` ${runLocation.fac}`;
            }
            runLocations.push(runLocationString);
            sites.add(runLocation.site);
        });
        runLocations = [...runLocations, ...sites];
        runLocations.sort();

        const options = Constants.convertArrayToSelectOptions(runLocations);
        options.unshift(Constants.convertValueToSelectValue('RUN_LOCATION', 'runLocation'));
        return options;
    }

    static getDataLocationOptions(siteFacilities) {
        let options = [Constants.convertValueToSelectValue('RUN_LOCATION', 'dataLocation')];
        if (siteFacilities) { // may not have been loaded yet on first render, so we have to check
            options = Constants.convertArrayToSelectOptions(siteFacilities);
            options.unshift(Constants.convertValueToSelectValue('RUN_LOCATION', 'dataLocation'));
        }

        return options;
    }

    static getCoordinateSystemOptionsForVariable(process) {
        const options = Object.keys(process.variable_retrieval.coordinate_systems).map((id) => {
            const coordSys = process.variable_retrieval.coordinate_systems[id];

            let dimensionLabel = '';
            const dims = Object.keys(coordSys.dimensions).map((coordId) => {
                return coordId;
            });
            if (dims.length > 0) {
                dimensionLabel = `(${dims.join(', ')})`;
            }

            return { value: id, label: `${id} ${dimensionLabel}` };
        });

        // We also need to add the NO_TRANSFORMATION option
        options.push({ value: '', label: 'No Transformation' });

        return options;
    }

    static getSelectedSelectValue(value, selectList) {
        let selectValue = null;
        selectList.forEach(selectItem => {
            if (selectItem.value === value || (selectItem.value === '' & value === null)) {
                selectValue = selectItem;
            }
        });

        return selectValue;
    }

    static getCoordinateSystemSelectValue(value, coordinateSystemList) {
       return ProcessUtils.getSelectedSelectValue(value, coordinateSystemList);
    }

    static getTransformVariableCoordinateSystem(process, parameterName, variableName) {
        let variableCoordinateSystem = null;
        if (this.parameterIsDimensionSpecific(parameterName)) {
            variableCoordinateSystem = process.variable_retrieval.retrieved_variables[variableName].coordinate_system;
        }
        return variableCoordinateSystem;
    }

    static getOutputDatstreamOptionsForVariable(process) {
        return Constants.convertArrayToSelectOptions(process.output_datastreams);
    }

    static getDatastreamVariablesForDod(datastreamName, includeCVars) {
        const dodsById = DodService.getMyState(DodService.getStore().getState()).dodsById;
        let variables = null;
        const variablesList = dodsById[datastreamName];

        if (variablesList) {
            variables = {};
            Object.keys(variablesList).forEach((variableName) => {
                // Default is we don't want to include cVars as option, but
                // for units validation, then we need to use cVars
                if (includeCVars || variableName !== 'cVars') {
                    variables[variableName] = dodsById[datastreamName][variableName];
                }
            });
        }

        return variables;
    }

    static getPrimaryDatastream(process, inputDatasetName) {
        const inputDataset = process.variable_retrieval.input_datasets[inputDatasetName];
        let datastream = '';
        if (inputDataset && inputDataset.rules.length > 0 && inputDataset.rules[0].datastream_name) {
            datastream = inputDataset.rules[0].datastream_name;
        }
        return datastream;
    }

    static getInputDatasetDatastreamsLists(process, inputDatasetName) {
        const inputDataset = process.variable_retrieval.input_datasets[inputDatasetName];

        const datastreamsList = [];
        if (inputDataset && inputDataset.rules.length > 0) {
            inputDataset.rules.forEach(rule => {
                const datastream = rule.datastream_name;
                if (datastreamsList.indexOf(datastream) === -1) {
                    datastreamsList.push(datastream);
                }
            });
        }

        let tooltip = false;
        let visibleList = datastreamsList[0];
        let fullList = datastreamsList.join(', ');

        if (datastreamsList.length === 2) {
            visibleList = fullList;

        } else if (datastreamsList.length > 2) {
            visibleList = `${datastreamsList.slice(0,2).join(', ')} ...`;
            tooltip = true;
        }

        return { visibleList, fullList, tooltip };
    }

    /**
     * There should only be one.  If there is more than one, then this is an error condition and needs to be flaggd.
     * @param process
     * @param outputDatastreamName
     */
    static getCoordinateSystemsForOutputDatastream(process, outputDatastreamName) {
        const coordinateSystems = [];
        const mappings = process.variable_retrieval.output_datastream_variable_mappings[outputDatastreamName];
        if (mappings) {
            Object.keys(mappings).forEach(retrievedVariableName => {
                const retrievedVariable = process.variable_retrieval.retrieved_variables[retrievedVariableName];
                const coordinateSystemName = retrievedVariable.coordinate_system;
                if (coordinateSystemName) {
                    if (!_.includes(coordinateSystems, coordinateSystemName)) {
                        coordinateSystems.push(coordinateSystemName);
                    }
                }
            });
        }
        return coordinateSystems;
    }

    static getRetrievedVariableDimensions(process, retrievedVariableName) {
        const dims = process.variable_retrieval.retrieved_variables[retrievedVariableName].dims;
        return `(${ProcessUtils.getDimensionsAsString(dims).fullList})`;
    }

    static getRetrievedVariableUnits(process, retrievedVariableName) {
        const units = process.variable_retrieval.retrieved_variables[retrievedVariableName].units;
        return ProcessUtils.getUnits(units);
    }

    static getRetrievedVariableDimensionsFromInputstream(process, retrievedVariableName) {
        let dimensions = [];

        // Look up the input datastream variable name
        const inputDatasetName = process.variable_retrieval.retrieved_variables[retrievedVariableName].input_dataset;
        const inputDataset = process.variable_retrieval.input_datasets[inputDatasetName];

        // Since all datastream variables mapped to this retrieved variable will have the same dimensions,
        // we can just pick the datastream dimensions from the first rule.
        if (inputDataset.rules.length > 0) {
            const rule = inputDataset.rules[0];
            const datastreamVariables = rule.variable_mappings[retrievedVariableName];
            const datastreamName = rule.datastream_name;

            datastreamVariables.forEach(datastreamVariableName => {
                // Look up the dimensions from the dods list
                const dodVar = ProcessUtils.getDatastreamVariable(datastreamName, datastreamVariableName);
                if (dodVar) {
                    dimensions = dodVar.dims;
                }
            });

        }

        return dimensions;
    }

    /**
     * Get all input datastreams variable names used in this process for the given datastream.
     * @param process
     * @param inputDatasetName
     */
    static getOriginalVariableNamesFromInputstream(process, inputDatasetName) {
        const variableNames = {};
        ProcessUtils.visitAllInputDatasetRules(process, (rule, inputDataset) => {
            const datastreamName = rule.datastream_name;
            if (datastreamName === inputDatasetName) {
                Object.values(rule.variable_mappings).forEach(inputVariableNames => {
                    inputVariableNames.forEach(variableName => {
                        variableNames[variableName] = true;
                    });
                });
            }
        });
        return variableNames;
    }

    /**
     * For the given retrieved variable, find the original datastream variable name(s) by
     * looking through the variable mappings in the input dataset rules.
     * Note that there could be more than one name if the variable name changed over time, so this
     * returns an array.
     * @param {} process
     * @param {*} retrievedVariableName
     * @param {*} inputDatastreamName
     */
    static getOriginalVariableNameFromInputstream(process, retrievedVariableName, inputDatastreamName) {
        const Set = require('es6-set');
        const originalNames = new Set();

        // Look up the input datastream variable name
        const inputDatasetName = process.variable_retrieval.retrieved_variables[retrievedVariableName].input_dataset;

        ProcessUtils.visitInputDatasetRules(process, inputDatasetName, (rule) => {
            if (rule.datastream_name === inputDatastreamName) {
                const datastreamVariableNames = rule.variable_mappings[retrievedVariableName];
                datastreamVariableNames.forEach(name => {
                    originalNames.add(name);
                });
            }
        });

        return [...originalNames];
    }

    /**
     * For the given retrieved variable, find what the units were from the original input datastream.  Return an
     * array in the case that the retrieved variable is populated by multiple input datastreams with different
     * units.
     * @param process
     * @param retrievedVariableName
     * @returns [] - array of units
     */
    static getOriginalVariableUnits(process, retrievedVariableName) {
        const originalUnits = {};

        // Look up the input datastream variable name
        const inputDatasetName = process.variable_retrieval.retrieved_variables[retrievedVariableName].input_dataset;

        ProcessUtils.visitInputDatasetRules(process, inputDatasetName, (rule) => {
            const datastreamVariableNames = rule.variable_mappings[retrievedVariableName];
            const datastreamName = rule.datastream_name;
            const dodVariables = ProcessUtils.getDatastreamVariablesForDod(datastreamName, true);
            if (datastreamVariableNames) {
                datastreamVariableNames.forEach(datastreamVariableName => {
                    if (dodVariables) {
                        let variable = dodVariables[datastreamVariableName];
                        if (!variable) {
                            // variable might be a coordinate variable, so it is stored in a special spot
                            variable = dodVariables.cVars[datastreamVariableName];
                        }
                        if (variable) { // Some datastreams may have no variables defined in the dod
                            const units = variable.units;
                            if (units) {
                                originalUnits[units] = '';
                            }
                        }
                    }
                });

            }
        });

        return Object.keys(originalUnits);
    }

    /**
     * Get units label
     * @param units
     * @returns string
     */
    static getUnits(units) {
        const value = Constants.getOptionById(units, Constants.RETRIEVED_VARIABLE_UNITS);
        if (value === null) {
            return units;
        }
        return value.label;
    }

    /**
     * Get value object for RIEUnits
     * @param units string
     * @returns object
     */
    static getUnitsSelectValue(units) {
        let value = Constants.getOptionById(units, Constants.RETRIEVED_VARIABLE_UNITS);
        if (value === null) {
            value = Constants.convertValueToSelectValue(units);
        }
        return value;
    }

    static getRetrievedVariableOutputDatastreams(process, retrievedVariableName) {
        const outputDatastreams = process.variable_retrieval.output_datastream_variable_mappings;
        const variableDatastreams = [];
        for (const datastream in outputDatastreams) {
            const dsVars = outputDatastreams[datastream];
            for (const dsVariable in dsVars) {
                if (dsVariable === retrievedVariableName) {
                    variableDatastreams.push(datastream);
                }
            }
        }
        return variableDatastreams;
    }

    static getRetrievedVariableInputDatastreams(process, retrievedVariableNameOrArrayOfNames) {
        let retrievedVariableNames = retrievedVariableNameOrArrayOfNames;
        if (typeof retrievedVariableNames == 'string') {
            retrievedVariableNames = [retrievedVariableNameOrArrayOfNames];
        }
        const inputDatastreams = {};
        retrievedVariableNames.forEach(retrievedVariableName => {
            const retrievedVariable = process.variable_retrieval.retrieved_variables[retrievedVariableName];
            const inputDataset = process.variable_retrieval.input_datasets[retrievedVariable.input_dataset];

            inputDataset.rules.forEach(rule => {
                const datastreamName = rule.datastream_name;
                inputDatastreams[datastreamName] = datastreamName;
            });
        });
        return Object.keys(inputDatastreams);
    }

    static getDimensionsAsString(dimsArray) {
        let visibleList = '()';
        let fullList = null;
        let tooltip = false;

        if (dimsArray !== null) {
            fullList = dimsArray.join(', ');

            if (dimsArray.length > 2) {
                visibleList = `(${dimsArray.slice(0,2).join(', ')} ...)`;
                tooltip = true;
            } else {
                visibleList = `(${fullList})`;
            }
        }

        return { visibleList, fullList, tooltip };
    }

    static getCoordinateSystemsMappedToDataset(process, inputDatasetName) {
        const linkedCoordinateSystems = [];
        ProcessUtils.visitDimensions(process, (dimension, coordinateSystemName) => {
            if (dimension.gridding_method === Constants.COORDINATE_SYSTEM_GRIDDING_METHOD_MAPPING &&
                dimension.input_dataset === inputDatasetName) {
                linkedCoordinateSystems.push(coordinateSystemName);
            }
        });
        return linkedCoordinateSystems;
    }

    /**
     * Visitor method that iterates through all the retrieved variables in a process
     * @param process
     * @param visitFunction
     */
    static visitRetrievedVariables(process, visitFunction) {
        const retrievedVariables = _.get(process, 'variable_retrieval.retrieved_variables');

        if (retrievedVariables) {
            Object.keys(retrievedVariables).forEach(retrievedVariableName => {
                const retrievedVariable = retrievedVariables[retrievedVariableName];
                visitFunction(retrievedVariable);
            });
        }
    }

    static getRetrievedVariablesForDataset(process, inputDatasetName) {
        const linkedVariables = [];
        ProcessUtils.visitRetrievedVariables(process, (retrievedVariable) => {
            if (retrievedVariable.input_dataset === inputDatasetName) {
                linkedVariables.push(retrievedVariable.name);
            }
        });
        return linkedVariables;
    }

    static getRetrievedVariablesForDatastream(process, inputDatastreamName) {
        const variables = [];
        ProcessUtils.visitRetrievedVariables(process, (retrievedVariable) => {
            const datastreams = ProcessUtils.getRetrievedVariableInputDatastreams(process, retrievedVariable.name);
            if (datastreams.indexOf(inputDatastreamName) >= 0) {
                variables.push(retrievedVariable.name);
            }
        });
        return variables;
    }

    static getInputDatastreamsForDataset(process, inputDatasetName) {
        const inputDatastreams = [];
        ProcessUtils.visitInputDatasetRules(process, inputDatasetName, (rule) => {
            const ruleDatastream = rule.datastream_name;
            if (inputDatastreams.indexOf(ruleDatastream) === -1) {
                inputDatastreams.push(ruleDatastream);
            }
        });
        return inputDatastreams;
    }

    static getOutputstreamVariableOptions(process, retrievedVariableName) {

        const outputDatastreamName = this.getRetrievedVariableOutputDatastreams(process, retrievedVariableName);


        let outputstreamVariables = [];
        if (ProcessUtils.getDatastreamVariablesForDod(outputDatastreamName)) {
            outputstreamVariables = Object.keys(ProcessUtils.getDatastreamVariablesForDod(outputDatastreamName));
        }

        const options = Constants.convertArrayToSelectOptions(outputstreamVariables);
        Constants.sortSelectOptions(options);
        return options;
    };

    static sortRetrievedVariablesByDataset(process) {
        const sortedRetrievedVariableIds = [];
        const sortedInputDatasetIds = [];


        // Sort the input datasets so that any empty ones (i.e., no mapped variables) are at the bottom
        const sortedIds = Object.keys(process.variable_retrieval.input_datasets).sort((a, b) => {
            return a.toLowerCase().localeCompare(b.toLowerCase());
        });
        const emptyDatasets = [];

        sortedIds.forEach((datasetId) => {
            // If the dataset has no variables, move it to the end of the list
            const variablesForDataset = this.getRetrievedVariablesForDataset(process, datasetId);

            if (variablesForDataset.length === 0) {
                emptyDatasets.push(datasetId);
            } else {
                sortedInputDatasetIds.push(datasetId);
            }
        });
        emptyDatasets.forEach((datasetId) => {
            sortedInputDatasetIds.push(datasetId);
        });

        // sort the variables so they are in the correct order
        const retrievedVariables = process.variable_retrieval.retrieved_variables;

        sortedInputDatasetIds.forEach((datasetId) => {
            Object.keys(retrievedVariables).forEach(variableId => {
                const variable = retrievedVariables[variableId];
                if (variable.input_dataset === datasetId) {
                    sortedRetrievedVariableIds.push(variableId);
                }
            });
        });

        return { sortedRetrievedVariableIds, sortedInputDatasetIds };
    }

    /**
     * Visitor method that iterates through all the dimensions in a process
     * @param process
     * @param visitFunction
     */
    static visitDimensions(process, visitFunction) {
        const coordinateSystems = _.get(process, 'variable_retrieval.coordinate_systems');

        if (coordinateSystems) {
            Object.keys(coordinateSystems).forEach(coordinateSystemName => {
                const coordinateSystem = coordinateSystems[coordinateSystemName];
                Object.keys(coordinateSystem.dimensions).forEach(dimensionName => {
                    const dimension = coordinateSystem.dimensions[dimensionName];
                    visitFunction(dimension, coordinateSystemName);
                });
            });
        }
    }

    static visitNonPassthroughDimensions(process, visitFunction) {
        ProcessUtils.visitDimensions(process, (dimension, coordinateSystemName) => {
            const nonPassthroughDimensions = ProcessUtils.getNonPassthroughDimensionNames(process, coordinateSystemName);
            if (nonPassthroughDimensions.indexOf(dimension.name) > -1) {
                visitFunction(dimension, coordinateSystemName);
            }
        });
    }

    static visitInputDatasets(process, visitFunction) {
        const inputDatasets = _.get(process, 'variable_retrieval.input_datasets');

        Object.keys(inputDatasets).forEach(inputDatasetName => {
            const inputDataset = inputDatasets[inputDatasetName];
            visitFunction(inputDataset);
        });
    }

    /**
     * Visit all rules across all datasets
     * @param {} process
     * @param {*} visitFunction
     */
    static visitAllInputDatasetRules(process, visitFunction) {
        const inputDatasets = _.get(process, 'variable_retrieval.input_datasets');

        Object.keys(inputDatasets).forEach(inputDatasetName => {
            const inputDataset = inputDatasets[inputDatasetName];
            inputDataset.rules.forEach(rule => {
                visitFunction(rule, inputDataset);
            });
        });
    }

    static getDimensionUnitsLabel(dimension) {
        let unitsLabel = '';
        if (dimension.name === 'time') {
            unitsLabel = Constants.UNIT_SECONDS_LABEL;
        } else {
            unitsLabel = dimension.coordinate_variable_units;
        }

        return unitsLabel;
    }

    /**
     * Visit all rules for the given dataset
     * @param {} process
     * @param {*} visitFunction
     */
    static visitInputDatasetRules(process, inputDatasetName, visitFunction) {
        const inputDataset = process.variable_retrieval.input_datasets[inputDatasetName];
        inputDataset.rules.forEach(rule => {
            visitFunction(rule, inputDataset);
        });
    }

    static getDefaultOutputDatastreamName(process) {
        let outputDatastreamName = '';
        if (process.output_datastreams.length > 0) {
            // TODO: give preference to dod on the fly if one exists in list?
            outputDatastreamName = process.output_datastreams[0];
        }

        if (outputDatastreamName === '') {
            let strippedProcessName = process.name.replace(/\W/g, '');
            strippedProcessName = strippedProcessName.replace(/_/g, '');
            if (strippedProcessName.length > 18) {
                //output datastream can't be longer that 24 characters (6 from 'ouput')
                strippedProcessName = strippedProcessName.substring(0, 18);
            }
            outputDatastreamName = `${strippedProcessName}output.c1`;
        }

        return outputDatastreamName;
    }

    static getLocationDisplayText(siteFacilityObject, type) {
        let locationText = siteFacilityObject.site;

        if (locationText === 'RUN_LOCATION') {
            locationText = Constants.getRunLocationLabel(type);
        }
        locationText += siteFacilityObject.fac ? ` ${siteFacilityObject.fac}` : '';
        return locationText;
    }

    static getTimeDisplayText(time) {
        let timeText = '';
        if (time === 'ANY_TIME') {
            timeText = 'Any time';
        } else {
            timeText = this.epochToMoment(time).format('YYYY-MM-DD');
        }
        return timeText;
    }

    static epochToMoment(epoch) {
        return moment(new Date(epoch * 1000));
    }

    static momentToEpoch(date) {
        return new Date(date).getTime() / 1000.0;
    }

    static calculateGridNumberOfIntervals(interval, start, end) {
        if (interval === 0) {
            // will cause not real number error to divide by 0
            return 0;
        }

        return Math.round(((end - start) / interval) + 1);
    }

    static calculateGridEnd(interval, length, start, end) {
        // if there is no end, default is equal to start value
        //end = end ? end : start;

        if (interval !== 0 && length > 0) {
            end = start + (interval * (length - 1));
        } else {
            //hm this might start some odd behavior if they set end before interval
            end = start + (end - interval);
        }
        return end;
    }

    static calculateGridInterval(interval, length, start, end) {
        if (interval == 0 && length > 0) {
            return Math.round((end - start) / length);
        } else if (length == 0) {
            return 0;
        }

        return interval;

    }

    /**
     * Get process' coordinate system name for wizard mode
     * @param process
     * @returns {string}
     */
    static getWizardCoordinateSystemName(process) {
        let name = '';

        const coordinateSystems = process.variable_retrieval.coordinate_systems;
        if (Object.keys(coordinateSystems).length > 0) {
            name = coordinateSystems[Object.keys(coordinateSystems)[0]].name;
        }

        return name;
    }

    /**
     * List of dimensions defined for process's coordinate system in wizard mode
     * @returns {Array}
     */
    static getOutputDimensionsList(process) {
        let dimensions = [];


        const coordinateSystems = process.variable_retrieval.coordinate_systems;
        if (Object.keys(coordinateSystems).length > 0) {
            dimensions = Object.keys(coordinateSystems[this.getWizardCoordinateSystemName(process)].dimensions);
        }

        return dimensions;
    }
}



// WEBPACK FOOTER //
// ./src/utils/ProcessUtils.js